import { Head, Link, useMutation, useRouter } from 'arcway/lib/client'

import { AuthPageShell } from '#components/auth-page-shell.jsx'
import { Button } from '#components/ui/button.jsx'
import { ErrorAlert } from '#components/ui/error-alert.jsx'
import { Field, FieldGroup, FieldLabel } from '#components/ui/field.jsx'
import { Input } from '#components/ui/input.jsx'
import { PasswordInput } from '#components/ui/password-input.jsx'

import { useGuestSession } from '../hooks/use-guest-session.js'
import {
  authEmailPrefill,
  authSwitchHref,
  completeAuth,
} from '../lib/client/auth.js'
import { safeReturnPath } from '../lib/client/safe-return-path.js'

export default function LoginPage() {
  const router = useRouter()
  const next = safeReturnPath(router.query.next)
  const email = authEmailPrefill(router.query.email)
  const session = useGuestSession(next)
  const request = useMutation('/auth/login', { method: 'POST' })

  async function handleSubmit(event) {
    event.preventDefault()
    if (request.loading) return

    const data = new FormData(event.currentTarget)
    try {
      await completeAuth(request, session, router, next, {
        email: data.get('email'),
        password: data.get('password'),
      })
    } catch {}
  }

  return (
    <>
      <Head
        description='Log in to manage your Mono podcasts.'
        title='Log in — mono.fm'
      />
      <AuthPageShell
        kind='login'
        description='Log in to manage your shows, episodes, and account.'
        loading={session.loading || session.authenticated}
        title='Welcome back'
      >
        <form className='space-y-6' onSubmit={handleSubmit}>
          {request.error && <ErrorAlert>{request.error.message}</ErrorAlert>}
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor='email'>Email</FieldLabel>
              <Input
                autoComplete='email'
                defaultValue={email}
                disabled={request.loading}
                id='email'
                maxLength='320'
                name='email'
                placeholder='you@example.com'
                required
                type='email'
              />
            </Field>
            <Field>
              <div className='flex items-center justify-between gap-4'>
                <FieldLabel htmlFor='password'>Password</FieldLabel>
                <Link
                  className='text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline'
                  href='/auth/reset-password/request'
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                autoComplete='current-password'
                disabled={request.loading}
                id='password'
                maxLength='1024'
                minLength='1'
                name='password'
                required
                type='password'
              />
            </Field>
          </FieldGroup>
          <Button
            className='w-full'
            loading={request.loading}
            size='lg'
            type='submit'
          >
            Log in
          </Button>
          {request.loading && (
            <p className='auth-pending' role='status'>
              Logging you in…
            </p>
          )}
        </form>
        <p className='mt-6 text-center text-sm text-muted-foreground'>
          New to Mono?{' '}
          <Link
            className='font-medium text-foreground underline-offset-4 hover:underline'
            href={authSwitchHref('/signup', router.query.next)}
          >
            Create an account
          </Link>
        </p>
        <p className='mt-4 text-center text-xs text-muted-foreground'>
          Looking for your original Mono account?{' '}
          <a
            className='underline hover:text-foreground'
            href='https://app.mono.fm/login'
          >
            Use legacy login
          </a>
          .
        </p>
      </AuthPageShell>
    </>
  )
}
